import { useMutation } from '@apollo/client/react';
import { useCallback, useState } from 'react';
import type { CompanyRegistryCountry } from '@insio/validation';
import { UpdateAccountDocument, UpdatePartyDocument, type UpdatePartyInput } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useCompanyRegistryLookup } from './use-company-registry-lookup';
import { classifyCompanyIdInput } from '@/utils/company-id-input';
import { registryAddress, registryTaxIds } from '@/utils/company-registry-fill';
import { partyErrorMessage } from '@/utils/party-issues';

export interface CompanyRegistryFillOptions {
  /** null disables the fill — the country has no register we can query. */
  countryCode: CompanyRegistryCountry | null;
  /** Party to write the looked-up details onto; null disables the fill. */
  partyId: string | null | undefined;
}

export interface CompanyRegistryFillState {
  loading: boolean;
  error: string | null;
  applied: boolean;
  /** Looks the company up and writes every returned field. True when applied. */
  apply: (rawQuery: string) => Promise<boolean>;
  /** Clears stale feedback so a corrected input isn't shown under an old error. */
  clearFeedback: () => void;
}

/**
 * Fills an organization's own details from its national business register.
 * Shared by the onboarding organization step and the organization settings tab.
 */
export function useCompanyRegistryFill({
  countryCode,
  partyId
}: CompanyRegistryFillOptions): CompanyRegistryFillState {
  const t = useFeatureTranslation('shared.companyRegistry');
  const lookup = useCompanyRegistryLookup(countryCode, { includeRegistryEntry: true });
  const [updateParty] = useMutation(UpdatePartyDocument);
  const [updateAccount] = useMutation(UpdateAccountDocument);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  const clearFeedback = useCallback(() => {
    setError(null);
    setApplied(false);
  }, []);

  const apply = useCallback(
    async (rawQuery: string): Promise<boolean> => {
      if (loading || !partyId || !countryCode) return false;

      const { normalized, error: inputError } = classifyCompanyIdInput(rawQuery, countryCode);
      if (inputError) {
        setError(t(`errors.${inputError}`));
        return false;
      }

      setError(null);
      setApplied(false);
      setLoading(true);
      try {
        const result = await lookup(normalized);
        if (!result) {
          setError(t('errors.notFound'));
          return false;
        }

        // One write for the party, one for the account name — the backend mirrors the
        // account name onto the main party, so it cannot be set through updateParty.
        const write = await updateParty({
          variables: {
            id: partyId,
            input: {
              companyId: result.companyId,
              ...registryTaxIds(result),
              // A register that names no entry leaves a manually entered one in place.
              ...(result.registryEntry ? { registryEntry: result.registryEntry } : {}),
              mainAddress: registryAddress(result, countryCode, '')
            } satisfies UpdatePartyInput
          }
        });

        const partyResult = write.data?.updateParty.result;
        if (partyResult && partyResult.errorCount > 0) {
          setError(partyErrorMessage(partyResult.issues) || t('errors.unavailable'));
          return false;
        }

        if (result.name) {
          await updateAccount({ variables: { input: { name: result.name } } });
        }

        setApplied(true);
        return true;
      } catch (e) {
        setError(t('errors.unavailable'));
        // eslint-disable-next-line no-console
        console.warn('Company registry lookup failed:', e);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [loading, partyId, countryCode, t, lookup, updateParty, updateAccount]
  );

  return { loading, error, applied, apply, clearFeedback };
}
